// CollapsibleSection.jsx — Lightweight accordion-style wrapper used inside the
// Settings page. The previous Settings page rendered ~15 panels stacked
// vertically; on mobile the user had to scroll forever. This wrapper groups
// them into a few labelled buckets that the user opens on demand.

import React, { useState, useEffect, createContext, useContext } from "react";

// ─── v119.209 — iOS-style Settings categories ─────────────────────────────
// Settings sections declare themselves in CATEGORY_MAP; SettingsTabsBar renders
// the pills and CollapsibleSection hides itself when it doesn't match.
export const SETTINGS_CATS = [
  { id: "general",  label: "🏠 عام" },
  { id: "integr",   label: "🔌 التكاملات" },
  { id: "perms",    label: "🧩 الصلاحيات" },
  { id: "flows",    label: "🔄 الإجراءات" },
  { id: "marketing", label: "📣 التسويق" },
  { id: "advanced", label: "🛠 متقدم" },
];

export const CATEGORY_MAP = {
  files: "general",
  "company-identity": "general",
  categories: "general",
  "system-manual": "general",
  "integrations-hub": "integr",
  "users-perm": "perms",
  "perm-boxes": "perms",
  "req-templates": "flows",
  "sop-engine": "flows",
  "notify-goals": "flows",
  "tech-advisor": "advanced",
  // v190.8 fix — الذكاء الميداني ولوحة حوكمة Emergent كانتا تظهران في كل
  // تبويبات الإعدادات الخمسة لأن الـid لم يُسجَّل في CATEGORY_MAP فيعود
  // undefined ← يفشل شرط الإخفاء في السطر 99.
  "field-intel": "advanced",
  "emergent-governance": "advanced",
  "data-backup": "advanced",
  // ⑤/L3-أ2 — كان غير مسجّل فيظهر في كل التبويبات؛ مكانه تبويب التسويق.
  "marketing-min-age": "marketing",
};

const SettingsCatContext = createContext(null);

export function SettingsCatProvider({ children }) {
  const [cat, setCat] = useState("general");
  return (
    <SettingsCatContext.Provider value={{ cat, setCat }}>
      {children}
    </SettingsCatContext.Provider>
  );
}

export function SettingsTabsBar() {
  const ctx = useContext(SettingsCatContext);
  if (!ctx) return null;
  return (
    <div data-testid="settings-ios-tabs"
         style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4,
                  padding: 5, background: "rgba(120,113,108,0.08)", borderRadius: 14 }}>
      {SETTINGS_CATS.map((c) => (
        <button key={c.id} type="button"
                data-testid={`settings-cat-${c.id}`}
                onClick={() => ctx.setCat(c.id)}
                style={{
                  flex: "1 1 auto", border: "none", cursor: "pointer",
                  padding: "9px 14px", borderRadius: 10, fontSize: 12.5, fontWeight: 800,
                  fontFamily: "inherit",
                  background: ctx.cat === c.id ? "#fff" : "transparent",
                  color: "var(--brown-deep)",
                  boxShadow: ctx.cat === c.id ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
                  transition: "background .15s ease, box-shadow .15s ease",
                }}>
          {c.label}
        </button>
      ))}
    </div>
  );
}

export default function CollapsibleSection({
  id,
  title,
  subtitle,
  icon,
  count,            // optional badge (e.g. "16" files)
  defaultOpen = false,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const ctx = useContext(SettingsCatContext);

  // v119.92 — cross-panel deep-link: another page (e.g. MarketingPlansPanel)
  // can ask Settings to expand a specific section by dispatching a
  // window event `tw-open-section` with detail.id === this section id.
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.id === id) {
        setOpen(true);
        // jump to the right iOS category too
        if (ctx && CATEGORY_MAP[id]) ctx.setCat(CATEGORY_MAP[id]);
      }
    };
    window.addEventListener("tw-open-section", handler);
    return () => window.removeEventListener("tw-open-section", handler);
  }, [id, ctx]);

  // Hide when an iOS category is active and this section belongs elsewhere.
  if (ctx && CATEGORY_MAP[id] && CATEGORY_MAP[id] !== ctx.cat) return null;
  return (
    <div
      className="card"
      data-testid={`coll-${id}`}
      style={{
        marginTop: 12,
        padding: 0,
        overflow: "hidden",
        border: "1px solid var(--border)",
        borderRadius: 10,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`coll-toggle-${id}`}
        aria-expanded={open}
        style={{
          all: "unset",
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          boxSizing: "border-box",
          padding: "14px 16px",
          cursor: "pointer",
          background: open ? "rgba(212,175,55,0.08)" : "transparent",
          borderBottom: open ? "1px solid var(--border)" : "1px solid transparent",
          transition: "background 0.15s ease",
        }}
      >
        <span
          style={{
            fontSize: 13,
            width: 22,
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
            color: "var(--brown-deep)",
          }}
          aria-hidden
        >
          ▶
        </span>
        {icon && <span style={{ fontSize: 18 }}>{icon}</span>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "var(--brown-deep)" }}>
            {title}
            {typeof count === "number" && count > 0 && (
              <span
                style={{
                  marginInlineStart: 8,
                  background: "var(--gold)",
                  color: "#fff",
                  fontSize: 10.5,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 999,
                }}
              >
                {count}
              </span>
            )}
          </div>
          {subtitle && (
            <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 2, lineHeight: 1.5 }}>
              {subtitle}
            </div>
          )}
        </div>
      </button>
      {open && (
        <div style={{ padding: "14px 16px 18px" }} data-testid={`coll-body-${id}`}>
          {children}
        </div>
      )}
    </div>
  );
}
